import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import AppMenu from './AppMenu';
import AvatarDropdown from './AvatarDropdown';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchWithAuth } from '../api/apiClient';
import { Settings } from 'lucide-react';
import useAutoHideOnScroll from '../hooks/useAutoHideOnScroll';
import './Header.css';

// Muss mit dem Mobil-Breakpoint in Header.css (`@media (max-width: 768px)`) übereinstimmen.
const MOBILE_QUERY = '(max-width: 768px)';

export default function Header({
  currentUser,
  onLogout,
  onReloadCollection,
  onUploadCsv,
  isLoading,
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isCollection = location.pathname.startsWith('/collection');
  const isDecks     = location.pathname.startsWith('/decks');
  const isFriends   = location.pathname.startsWith('/friends');
  const isWantlist  = location.pathname.startsWith('/wantlist');
  const isPlaytest  = location.pathname.startsWith('/playtest');

  // Eine Quelle für „mobil": begrenzt sowohl das Auto-Hide als auch die
  // veröffentlichte Header-Höhe. Auf Desktop hängt dadurch kein Scroll-Listener.
  const [istMobil, setIstMobil] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const anwenden = () => setIstMobil(mq.matches);
    anwenden();
    mq.addEventListener('change', anwenden);
    return () => mq.removeEventListener('change', anwenden);
  }, []);

  // Ein offenes Menü friert den Header ein; ein Seitenwechsel blendet ihn ein.
  const isHidden = useAutoHideOnScroll({
    enabled: istMobil && !showMenu && !avatarOpen,
    resetKey: location.pathname,
  });

  // Aktuell sichtbare Header-Höhe veröffentlichen — Profil-Banner,
  // Editor-Werkzeugleiste und Kategorie-Header kleben darunter.
  // useLayoutEffect statt useEffect: die Variable muss vor dem ersten Frame
  // stimmen, sonst zeigt der erste gemalte Frame auf Mobil 0px statt der
  // tatsächlichen Header-Höhe.
  // getBoundingClientRect().height statt offsetHeight: offsetHeight rundet auf ganze
  // Pixel, die sticky Kopfzeile darunter positioniert sich subpixelgenau — die
  // Rundungsdifferenz zwischen beiden liefe sonst als Spalt zwischen Navbar und
  // Kopfzeile mit.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const anwenden = () => {
      const offset = istMobil && !isHidden ? el.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--app-header-offset', `${offset}px`);
    };
    anwenden();
    const beobachter = new ResizeObserver(anwenden);
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [isHidden, istMobil]);

  // Beim Abmelden verschwindet der Header — Variable nicht stehen lassen.
  useEffect(() => () => {
    document.documentElement.style.removeProperty('--app-header-offset');
  }, []);

  const { data: pendingRequests = [] } = useQuery({
    queryKey: ['pendingRequests'],
    queryFn: async () => {
      const res = await fetchWithAuth('/api/relationships/pending');
      return res.json();
    },
    enabled: !!currentUser,
    refetchInterval: 60_000,
  });
  const hasPendingRequests = pendingRequests.length > 0;

  return (
    <header ref={headerRef} className={`app-header${isHidden ? ' app-header--hidden' : ''}`}>
      <div className="app-header__inner">
        <span className="app-header__site-name" onClick={() => navigate('/collection')}>wirbauendeindeck.de</span>

        <nav className="app-header__nav">
          <button onClick={() => navigate('/collection')} className={`nav-btn${isCollection ? ' active' : ''}`}>
            Sammlung
          </button>
          <button onClick={() => navigate('/decks')} className={`nav-btn${isDecks ? ' active' : ''}`}>
            Decks
          </button>
          <button onClick={() => navigate('/friends')} className={`nav-btn${isFriends ? ' active' : ''}`}>
            Freunde
            {hasPendingRequests && <span className="nav-btn__badge" />}
          </button>
          <button onClick={() => navigate('/wantlist')} className={`nav-btn${isWantlist ? ' active' : ''}`}>
            Wantlist
          </button>
          <button onClick={() => navigate('/playtest')} className={`nav-btn${isPlaytest ? ' active' : ''}`}>
            Playtest
          </button>
        </nav>

        <div className="app-header__controls">
          <div className="header-menu-anchor">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="header-menu-btn"
              title="Einstellungen"
              aria-label="Einstellungen"
            >
              <Settings size={16} aria-hidden />
            </button>
            <AppMenu
              isOpen={showMenu}
              onClose={() => setShowMenu(false)}
              onReloadCollection={onReloadCollection}
              onUploadCsv={onUploadCsv}
              isLoading={isLoading}
            />
          </div>
          <AvatarDropdown
            displayName={currentUser?.displayName || currentUser?.email}
            onLogout={onLogout}
            onOpenChange={setAvatarOpen}
          />
        </div>
      </div>
    </header>
  );
}
